import React, { useContext, useEffect } from 'react';
import { AuthContext } from 'contexts';
import { useHistory } from 'react-router';
import { useAnsweringServiceDetails } from 'hooks';
import { SupraSubscriptionProductBillingModel } from '@bb/domain/dist/types';
import useOrgBillingModel from 'hooks/useOrgBillingModel';
import useOverduePaymentStage from 'hooks/useOverduePaymentStage';
import styled from 'styled-components';
import { Color } from '@bb/elements-web';
import PaymentBanner from './PaymentBanner';

/**
 * The banner to show when the logged-in user has an outstanding payment due.
 * The content of the banner changes depending on if the logged-in user is
 * responsible for paying the outstanding balance or not.
 */
const OutstandingPaymentBanner: React.FC = () => {
  const { auth } = useContext(AuthContext);
  const history = useHistory();

  const isGlobalAdmin = auth.roles[auth.role] >= auth.roles.GLOBAL_ADMIN;

  const { answeringServicePayer } = useAnsweringServiceDetails({
    viewingLevel: isGlobalAdmin ? 'org' : 'user',
    userId: auth.id,
  });
  const { supraSubscriptionPayer } = useOrgBillingModel();
  const {
    isUserSuspended,
    isOrgSuspended,
    isUserDeactivated,
    isOrgDeactivated,
  } = useOverduePaymentStage();

  // Determines if the logged-in user is responsible for paying for their own plan.
  const isUserPaying =
    answeringServicePayer === 'user' ||
    supraSubscriptionPayer === SupraSubscriptionProductBillingModel.Keyholder;

  // Determines if the org/brokerage is responsible for paying for the logged-in user's plan.
  // The supra subscription does not need to be considered in this check since org/brokerage is not an
  // option for who can pay.
  const isOrgPaying = answeringServicePayer === 'brokerage';

  const goToPayNowModalUser = () => {
    history.push(`/subscriptions_payments/user?payNow=true`);
  };
  const goToPayNowModalOrg = () => {
    history.push(`/subscriptions_payments/org?payNow=true`);
  };

  const banners: React.ComponentProps<typeof PaymentBanner>[] = [];

  if (
    !isUserSuspended &&
    !isOrgSuspended &&
    !isUserDeactivated &&
    !isOrgDeactivated
  )
    return null;

  if (isUserPaying) {
    if (isUserSuspended) {
      banners.push({
        message:
          'Your service(s) have been suspended due to an outstanding payment.',
        canCloseBanner: false,
        showPayNowButton: true,
        goToPayNowModal: goToPayNowModalUser,
      });
    }
    if (isUserDeactivated) {
      banners.push({
        message:
          'You have been unsubscribed from your service(s) due to an outstanding payment.',
        canCloseBanner: true,
        showPayNowButton: false,
      });
    }
  }

  if (isOrgPaying) {
    if (isOrgSuspended) {
      if (isGlobalAdmin) {
        banners.push({
          message:
            "Your brokerage service(s) have been suspended due to an outstanding payment. Please update your brokerage's card to resume service.",
          canCloseBanner: false,
          showPayNowButton: true,
          goToPayNowModal: goToPayNowModalOrg,
        });
      } else {
        banners.push({
          message:
            'Your service(s) have been suspended. Please contact your brokerage for more information.',
          canCloseBanner: false,
          showPayNowButton: false,
        });
      }
    }
    if (isOrgDeactivated) {
      banners.push({
        message:
          'You have been unsubscribed from your service(s) due to an outstanding payment. Please contact your brokerage for more information.',
        canCloseBanner: true,
        showPayNowButton: false,
      });
    }
  }

  return (
    <>
      <OverheadContainer>
        {banners.map((props, index) => (
          <PaymentBanner key={index} {...props} />
        ))}
      </OverheadContainer>
    </>
  );
};

export default OutstandingPaymentBanner;

const OverheadContainer = styled.div`
  display: grid;
  padding: 16px;
  gap: 8px;
`;
